import { isEqual } from "es-toolkit/compat";
import * as ExpoDevice from "expo-device";
import type { PropsWithChildren } from "react";
import React, {
  createContext,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type {
  PlatformKeyCode,
  PlaybackState,
  RemoteOutboundMessage,
} from "$modules/expo-tv-connect";
import {
  TV_CONNECT_MDNS_DISCOVERY_DOMAIN,
  TV_CONNECT_MDNS_SERVICE_TYPE,
} from "$modules/expo-tv-connect";
import {
  ExpoTvConnectRemoteModule,
  TV_CONNECT_ERROR_CODE,
  type Device,
  type DiscoveryEvent,
  type ErrorEvent,
  type MessageEvent,
  type StartDiscoveryOptions,
  type StopDiscoveryOptions,
} from "$modules/expo-tv-connect-remote";
import Modal from "$src/core/components/atoms/Modal";
import type { ModalRef } from "$src/core/components/atoms/Modal/types";
import TvConnectRemoteSheet from "$src/core/components/organisms/TvConnectRemoteSheet/TvConnectRemoteSheet";
import useAppStateEffect from "$src/core/hooks/use-app-state-effect";
import useAuth from "$src/core/hooks/use-auth";
import useConfig from "$src/core/hooks/use-config";
import useTranslation from "$src/core/hooks/use-translation";
import type { TvConnectConfig } from "$src/core/models/tv-connect";
import Logging from "$src/core/services/logging/logging";
import wait from "$src/core/utils/async/wait";
import getStableDeviceId from "$src/core/utils/device/get-stable-device-id";
import stripHtmlEntities from "$src/core/utils/html/strip-html-entities";
import CustomPlatform from "$src/core/utils/platform/platform";
import normalizeTvDeviceName from "$src/core/utils/tv-connect/normalize-tv-device-name";

type Status = "disconnected" | "connecting" | "connected";

const parsePlatformKeyCode = (key: unknown): PlatformKeyCode | null => {
  const code =
    typeof key === "number"
      ? key
      : typeof key === "string" && key.trim() !== ""
        ? Number(key)
        : Number.NaN;

  return Number.isFinite(code) ? code : null;
};

const parseSupportedKeys = (keys: unknown): PlatformKeyCode[] | undefined => {
  if (!Array.isArray(keys)) return undefined;

  const supportedKeys = keys
    .map(parsePlatformKeyCode)
    .filter((key): key is PlatformKeyCode => key !== null);

  return supportedKeys.length > 0 ? supportedKeys : undefined;
};

const parseAsset = (
  asset: Record<string, unknown> | undefined,
): PlaybackState["asset"] | undefined => {
  if (!asset) return undefined;

  return {
    ...(typeof asset.title === "string" && { title: asset.title }),
    ...(typeof asset.subtitle === "string" && { subtitle: asset.subtitle }),
    ...(typeof asset.thumbnail === "string" && { thumbnail: asset.thumbnail }),
    ...(typeof asset.contentId === "string" && { contentId: asset.contentId }),
    ...(typeof asset.contentType === "string" && {
      contentType: asset.contentType,
    }),
  };
};

const parsePlaybackState = (
  payload: Record<string, unknown> | undefined,
): PlaybackState | null => {
  if (!payload) return null;

  const { status } = payload;
  if (status === "idle") return { status: "idle" };

  const playerState =
    status === "playing" || status === "paused" ? status : null;

  if (!playerState) return null;

  const asset = parseAsset(
    payload.asset as Record<string, unknown> | undefined,
  );

  return {
    status: playerState,
    ...(typeof payload.currentTime === "number" && {
      currentTime: payload.currentTime,
    }),
    ...(typeof payload.duration === "number" && { duration: payload.duration }),
    ...(typeof payload.live === "boolean" && { live: payload.live }),
    ...(asset && { asset }),
    ...(typeof payload.ad === "boolean" && { ad: payload.ad }),
    ...(typeof payload.muted === "boolean" && { muted: payload.muted }),
    ...(typeof payload.trailer === "boolean" && { trailer: payload.trailer }),
    ...(typeof payload.ts === "number" && { ts: payload.ts }),
  };
};

/** Device capabilities reported by the host's `capabilities` message. */
export type ConnectedCapabilities = {
  volumeControl?: boolean;
  supportedKeys?: PlatformKeyCode[];
};

const parseCapabilities = (
  payload: Record<string, unknown> | undefined,
): ConnectedCapabilities | null => {
  if (!payload) return null;

  const supportedKeys = parseSupportedKeys(payload.supportedKeys);

  return {
    ...(typeof payload.volumeControl === "boolean" && {
      volumeControl: payload.volumeControl,
    }),
    ...(supportedKeys && { supportedKeys }),
  };
};

type TvConnectRemoteContextType = {
  status: Status;
  discoveredDevices: Device[];
  device: Device | null;
  capabilities: ConnectedCapabilities | null;
  playback: PlaybackState | null;
  startDiscovery: (options?: StartDiscoveryOptions) => Promise<void>;
  stopDiscovery: (options?: StopDiscoveryOptions) => Promise<void>;
  connect: (deviceId: string) => Promise<void>;
  disconnect: () => Promise<void>;
  send: (message: RemoteOutboundMessage) => Promise<void>;
  present: () => void;
  dismiss: () => void;
};

export const TvConnectRemoteContext =
  createContext<TvConnectRemoteContextType | null>(null);

const getInitialConnectedDevice = (): Device | null => {
  try {
    return ExpoTvConnectRemoteModule.getConnectedDevice();
  } catch (error) {
    Logging.error("Failed to get initial connected device", { error });
    return null;
  }
};

const normalizeDevice = (device: Device): Device => ({
  ...device,
  name: normalizeTvDeviceName(
    stripHtmlEntities(device.name ?? ""),
    device.platform,
  ),
});

export const isAdvertisingPairingCode = (device: Device) =>
  device.platform === "mdns" && Boolean(device.txt?.code);

/** Tries to get back to a TV after the OS drops the connection. */
const MAX_RESUME_ATTEMPTS = 3;

export const TvConnectRemoteProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const config = useConfig();
  const { status: authStatus, authorizePairing, profile } = useAuth();

  const initialConnectedDevice = getInitialConnectedDevice();

  const [discoveredDevices, setDiscoveredDevices] = useState<Device[]>([]);
  const [status, setStatus] = useState<Status>(() =>
    initialConnectedDevice?.id ? "connected" : "disconnected",
  );
  const [device, setDevice] = useState<Device | null>(() =>
    initialConnectedDevice ? normalizeDevice(initialConnectedDevice) : null,
  );
  const [capabilities, setCapabilities] =
    useState<ConnectedCapabilities | null>(null);
  const [playback, setPlayback] = useState<PlaybackState | null>(null);

  const connectPromiseRef = useRef<Promise<void> | null>(null);
  const connectGenerationRef = useRef(0);

  const disconnectPromiseRef = useRef<Promise<void> | null>(null);
  const disconnectGenerationRef = useRef(0);

  const wantedDeviceIdRef = useRef<string | null>(null);
  const resumeAttemptsRef = useRef(0);

  const pendingStartDiscoveryRef = useRef<Promise<void> | null>(null);
  const pendingStopDiscoveryRef = useRef<Promise<void> | null>(null);

  const remoteRef = useRef<ModalRef | null>(null);

  const configuredConfigRef = useRef<TvConnectConfig | null>(null);

  const ensureConfigured = useCallback(() => {
    if (isEqual(configuredConfigRef.current, config.tvConnect)) return;

    if (!config.tvConnect) {
      throw new Error("TV connect configuration not found");
    }

    Logging.debug("[TvConnectRemote] Configuring");

    ExpoTvConnectRemoteModule.configure({
      ...config.tvConnect,
      mdnsServiceType: TV_CONNECT_MDNS_SERVICE_TYPE,
      mdnsDiscoveryDomain: TV_CONNECT_MDNS_DISCOVERY_DOMAIN,
      clientId: getStableDeviceId(),
    });

    configuredConfigRef.current = config.tvConnect;
  }, [config.tvConnect]);

  useLayoutEffect(() => {
    ensureConfigured();
  }, [ensureConfigured]);

  const present = useCallback(() => {
    remoteRef.current?.present();
  }, []);

  const dismiss = useCallback(() => {
    remoteRef.current?.dismiss();
  }, []);

  const startDiscovery = useCallback(
    async (options?: StartDiscoveryOptions) => {
      if (pendingStartDiscoveryRef.current) {
        return pendingStartDiscoveryRef.current;
      }

      if (pendingStopDiscoveryRef.current) {
        await pendingStopDiscoveryRef.current;
        await wait(400);
      }

      Logging.debug("[TvConnectRemote] Starting discovery");

      const promise = ExpoTvConnectRemoteModule.startDiscovery(options);
      pendingStartDiscoveryRef.current = promise;

      try {
        return await promise;
      } finally {
        if (pendingStartDiscoveryRef.current === promise) {
          pendingStartDiscoveryRef.current = null;
        }
      }
    },
    [],
  );

  const stopDiscovery = useCallback(async (options?: StopDiscoveryOptions) => {
    if (pendingStopDiscoveryRef.current) {
      return pendingStopDiscoveryRef.current;
    }

    if (pendingStartDiscoveryRef.current) {
      await pendingStartDiscoveryRef.current;
      await wait(400);
    }

    Logging.debug("[TvConnectRemote] Stopping discovery");

    const promise = ExpoTvConnectRemoteModule.stopDiscovery(options);
    pendingStopDiscoveryRef.current = promise;

    try {
      return await promise;
    } finally {
      if (pendingStopDiscoveryRef.current === promise) {
        pendingStopDiscoveryRef.current = null;
      }
    }
  }, []);

  const connect = useCallback(async (deviceId: string) => {
    // First wait for any pending disconnection to complete
    if (disconnectPromiseRef.current) {
      await disconnectPromiseRef.current;
      await wait(400);
    }

    // Prevent concurrent connections, just return the pending one
    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    const generation = ++connectGenerationRef.current;

    const isStale = () => connectGenerationRef.current !== generation;

    const connectPromise = (async () => {
      const discovered = await ExpoTvConnectRemoteModule.getDiscoveredDevices();
      const discoveredDevice = discovered.find((d) => d.id === deviceId);

      if (!discoveredDevice) {
        throw new Error(`Device not found: ${deviceId}`);
      }

      try {
        Logging.debug("[TvConnectRemote] Connecting device", { id: deviceId });

        setStatus("connecting");
        setDevice(null);
        setPlayback(null);
        setCapabilities(null);

        if (isStale()) return;

        await ExpoTvConnectRemoteModule.connect(deviceId);

        if (isStale()) return;

        const connectedDevice = ExpoTvConnectRemoteModule.getConnectedDevice();
        if (!connectedDevice) {
          throw new Error("Failed to connect to device");
        }

        const normalizedDevice = normalizeDevice(connectedDevice);

        setDevice(normalizedDevice);
        setStatus("connected");
        setPlayback(null);
        setCapabilities(null);
      } catch (error) {
        if (isStale()) return;

        setStatus("disconnected");
        setDevice(null);
        setPlayback(null);
        setCapabilities(null);

        throw error;
      }
    })();

    connectPromiseRef.current = connectPromise;

    try {
      return await connectPromise;
    } finally {
      if (connectPromiseRef.current === connectPromise) {
        connectPromiseRef.current = null;
      }
    }
  }, []);

  const clearSession = useCallback(() => {
    setStatus("disconnected");
    setDevice(null);
    setPlayback(null);
    setCapabilities(null);
  }, []);

  const disconnect = useCallback(async () => {
    // First wait for any pending connection to complete
    if (connectPromiseRef.current) {
      await connectPromiseRef.current;
      await wait(400);
    }

    // Prevent concurrent disconnections, just return the pending one
    if (disconnectPromiseRef.current) {
      return disconnectPromiseRef.current;
    }

    const generation = ++disconnectGenerationRef.current;

    const isStale = () => disconnectGenerationRef.current !== generation;

    const connectedDevice = ExpoTvConnectRemoteModule.getConnectedDevice();
    if (!connectedDevice) {
      throw new Error("No device is connected");
    }

    const { id: deviceId } = connectedDevice;

    const disconnectPromise = (async () => {
      Logging.debug("[TvConnectRemote] Disconnecting device", { id: deviceId });

      // The viewer no longer wants this TV, so nothing should resume it.
      wantedDeviceIdRef.current = null;

      if (isStale()) return;

      await ExpoTvConnectRemoteModule.disconnect();

      if (isStale()) return;

      clearSession();
    })();

    disconnectPromiseRef.current = disconnectPromise;

    try {
      return await disconnectPromise;
    } finally {
      if (disconnectPromiseRef.current === disconnectPromise) {
        disconnectPromiseRef.current = null;
      }
    }
  }, [clearSession]);

  const send = useCallback(async (message: RemoteOutboundMessage) => {
    Logging.debug("[TvConnectRemote] Sending message", { message });

    try {
      await ExpoTvConnectRemoteModule.sendMessage(message);
    } catch (error) {
      Logging.error("Failed to send TV connect message", { error, message });
      throw error;
    }
  }, []);

  /* Authorize a pairing code the host offered, then tell it we're done. */
  const approvePairing = useEffectEvent((code: string) => {
    if (authStatus !== "authenticated") return;

    Logging.debug("[TvConnectRemote] Authorizing host pairing code");

    authorizePairing(code)
      .then(async (success) => {
        if (!success) {
          Logging.error("Host pairing code was not authorized");
          return;
        }

        const name = ExpoDevice.deviceName?.trim();

        await send({
          type: "ack",
          payload: {
            ...(profile && { profileId: profile.id }),
            ...(name && { name }),
          },
        });
      })
      .catch((error) => {
        Logging.error("Failed to authorize host pairing code", { error });
      });
  });

  /* Subscribe to device discovery */
  useEffect(() => {
    const upsertDevice = (discoveredDevice: Device) => {
      const normalizedDevice = normalizeDevice(discoveredDevice);

      setDiscoveredDevices((prev) => {
        const exists = prev.some((d) => d.id === normalizedDevice.id);
        if (!exists) {
          return [...prev, normalizedDevice];
        }

        return prev.map((d) =>
          d.id === normalizedDevice.id ? normalizedDevice : d,
        );
      });
    };

    ExpoTvConnectRemoteModule.getDiscoveredDevices()
      .then((_discoveredDevices) => {
        _discoveredDevices.forEach((discoveredDevice) => {
          const { id } = discoveredDevice;
          Logging.debug("[TvConnectRemote] Device added", { id });
          upsertDevice(discoveredDevice);
        });
      })
      .catch((error: unknown) => {
        Logging.error("[TvConnectRemote] Failed to get devices", { error });
      });

    const handleDeviceAdded = (discoveredDevice: Device) => {
      const { id } = discoveredDevice;
      Logging.debug("[TvConnectRemote] Device added", { id });
      upsertDevice(discoveredDevice);
    };

    const handleDeviceUpdated = (discoveredDevice: Device) => {
      const { id } = discoveredDevice;
      Logging.debug("[TvConnectRemote] Device updated", { id });
      upsertDevice(discoveredDevice);
    };

    const handleDeviceRemoved = (discoveredDevice: Device) => {
      const { id } = discoveredDevice;
      Logging.debug("[TvConnectRemote] Device removed", { id });
      setDiscoveredDevices((prev) => prev.filter((d) => d.id !== id));
    };

    const handleDiscoveryStarted = ({ platform }: DiscoveryEvent) => {
      Logging.debug("[TvConnectRemote] Discovery started", { platform });
      setDiscoveredDevices((prev) =>
        prev.filter((d) => d.platform !== platform),
      );
    };

    const handleDiscoveryStopped = ({ platform }: DiscoveryEvent) => {
      Logging.debug("[TvConnectRemote] Discovery stopped", { platform });
      setDiscoveredDevices((prev) =>
        prev.filter((d) => d.platform !== platform),
      );
    };

    const subscriptions = [
      ExpoTvConnectRemoteModule.addListener("onDeviceAdded", handleDeviceAdded),
      ExpoTvConnectRemoteModule.addListener(
        "onDeviceUpdated",
        handleDeviceUpdated,
      ),
      ExpoTvConnectRemoteModule.addListener(
        "onDeviceRemoved",
        handleDeviceRemoved,
      ),
      ExpoTvConnectRemoteModule.addListener(
        "onDiscoveryStarted",
        handleDiscoveryStarted,
      ),
      ExpoTvConnectRemoteModule.addListener(
        "onDiscoveryStopped",
        handleDiscoveryStopped,
      ),
    ];

    return () => subscriptions.forEach((s) => s.remove());
  }, []);

  /* Subscribe to connection lifecycle */
  useEffect(() => {
    const handleDeviceConnected = (connectedDevice: Device) => {
      Logging.debug("[TvConnectRemote] Device connected", {
        id: connectedDevice.id,
      });

      const normalizedDevice = normalizeDevice(connectedDevice);

      wantedDeviceIdRef.current = connectedDevice.id;
      resumeAttemptsRef.current = 0;

      setStatus("connected");
      setDevice(normalizedDevice);
    };

    const handleDeviceDisconnected = (connectedDevice: Device) => {
      Logging.debug("[TvConnectRemote] Device disconnected", {
        id: connectedDevice.id,
      });

      clearSession();

      dismiss();
    };

    const handleMessage = (message: MessageEvent) => {
      Logging.debug("[TvConnectRemote] Message received", { message });

      switch (message.type) {
        case "ack": {
          const code = message.payload?.code;
          if (code) approvePairing(code);
          break;
        }
        case "capabilities": {
          const next = parseCapabilities(message.payload);
          if (next) setCapabilities(next);
          break;
        }
        case "playbackState": {
          const next = parsePlaybackState(message.payload);
          if (next) setPlayback(next);
          break;
        }
        default: {
          break;
        }
      }
    };

    const handleError = (error: ErrorEvent) => {
      const isConnectFailure =
        error.code === TV_CONNECT_ERROR_CODE.deviceConnectionFailed ||
        error.code === TV_CONNECT_ERROR_CODE.launchFailed ||
        error.code === TV_CONNECT_ERROR_CODE.pairingFailed ||
        error.code === TV_CONNECT_ERROR_CODE.pairingRequired;

      // `Logging` only reads `.message` off real `Error` instances
      const extra = {
        error: new Error(error.message),
        code: error.code,
        platform: error.platform,
        ...(error.deviceId && { deviceId: error.deviceId }),
      };

      // Discovery/resolve failures depend on the network the device is on
      // (local network access, no reachable host) and the native service
      // already recovers by stopping discovery itself.
      if (!isConnectFailure) {
        Logging.debug("[TvConnectRemote] Error received", extra);
        return;
      }

      Logging.error("[TvConnectRemote] Error received", extra);

      setStatus("disconnected");
      setDevice(null);
      setPlayback(null);
      setCapabilities(null);

      dismiss();
    };

    const subscriptions = [
      ExpoTvConnectRemoteModule.addListener(
        "onDeviceConnected",
        handleDeviceConnected,
      ),
      ExpoTvConnectRemoteModule.addListener(
        "onDeviceDisconnected",
        handleDeviceDisconnected,
      ),
      ExpoTvConnectRemoteModule.addListener("onMessage", handleMessage),
      ExpoTvConnectRemoteModule.addListener("onError", handleError),
    ];

    return () => subscriptions.forEach((s) => s.remove());
  }, [clearSession, dismiss]);

  // Reconnect to the TV the viewer still wants as soon as discovery finds it
  // again. The host remembers approved clients, so this asks them nothing.
  const resumeConnection = useEffectEvent(() => {
    const deviceId = wantedDeviceIdRef.current;
    if (!deviceId) return;
    if (status !== "disconnected") return;
    if (!discoveredDevices.some((_device) => _device.id === deviceId)) return;

    // A TV can be announcing itself a moment before it accepts connections, so
    // allow a few tries — bounded, or a TV that keeps refusing would retry on
    // every announcement for as long as the app is open.
    if (resumeAttemptsRef.current >= MAX_RESUME_ATTEMPTS) {
      wantedDeviceIdRef.current = null;
      Logging.error("Gave up resuming the TV connect session", {
        id: deviceId,
      });
      return;
    }

    resumeAttemptsRef.current += 1;

    Logging.debug("[TvConnectRemote] Resuming dropped connection", {
      id: deviceId,
      attempt: resumeAttemptsRef.current,
    });

    connect(deviceId).catch((error) => {
      Logging.debug("[TvConnectRemote] Resume attempt failed", { error });
    });
  });

  /* Re-establish a connection the OS dropped once the TV is discovered again */
  useEffect(() => {
    resumeConnection();
  }, [discoveredDevices, status]);

  /* Recognise a socket the OS tore down while the app was suspended */
  useAppStateEffect(
    (appState) => {
      // The native side may never report a socket the OS killed under it, so
      // trust the module over our own state on the way back in. Without this
      // the UI keeps claiming a connection whose commands go nowhere.
      if (appState !== "active") return;
      if (status !== "connected") return;
      if (ExpoTvConnectRemoteModule.getConnectedDevice()) return;

      Logging.debug("[TvConnectRemote] Connection lost while backgrounded");

      clearSession();

      dismiss();
    },
    [status, clearSession, dismiss],
  );

  /* Discover mDNS hosts while the app is active */
  useAppStateEffect(
    (appState) => {
      ensureConfigured();

      const abortController = new AbortController();
      const { signal } = abortController;

      const execute = async () => {
        if (signal.aborted) return;

        if (appState !== "background") {
          await startDiscovery({ platforms: ["mdns"] });
        } else {
          await stopDiscovery({ platforms: ["mdns"] });
        }
      };

      execute().catch((error) => {
        if (signal.aborted) return;

        Logging.error("Failed to start/stop discovery", { error });
      });

      return () => {
        abortController.abort();
      };
    },
    [ensureConfigured, startDiscovery, stopDiscovery],
  );

  /* Stop discovery when the context unmounts */
  useEffect(
    () => () => {
      stopDiscovery({ platforms: ["mdns"] }).catch((error) => {
        Logging.error("Failed to stop discovery", { error });
      });
    },
    [stopDiscovery],
  );

  const value = useMemo<TvConnectRemoteContextType>(
    () => ({
      discoveredDevices,
      device,
      status,
      capabilities,
      playback,
      startDiscovery,
      stopDiscovery,
      connect,
      disconnect,
      send,
      present,
      dismiss,
    }),
    [
      discoveredDevices,
      device,
      status,
      capabilities,
      playback,
      startDiscovery,
      stopDiscovery,
      connect,
      disconnect,
      send,
      present,
      dismiss,
    ],
  );

  return (
    <TvConnectRemoteContext.Provider value={value}>
      {children}

      <Modal ref={remoteRef} detents={["auto"]} title={t("TV_REMOTE/TITLE")}>
        {device && (
          <TvConnectRemoteSheet
            device={device}
            capabilities={capabilities}
            onDisconnect={disconnect}
            onSend={send}
          />
        )}
      </Modal>
    </TvConnectRemoteContext.Provider>
  );
};

export default CustomPlatform.select<React.FC<PropsWithChildren>>({
  native: TvConnectRemoteProvider,
  default: ({ children }: PropsWithChildren) => children,
});
